import { InboxOutlined } from '@ant-design/icons'
import { Button, Col, Form, Input, notification, Row, Select, Spin, Upload, Image } from 'antd'
import { UploadChangeParam } from 'antd/lib/upload'
import { UploadFile } from 'antd/lib/upload/interface'
import { last } from 'lodash'
import React, { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { placeService } from '../../api/api-place'
import { CDNFolders, resizeImage, sanitizeFileOrFolder } from '../../imagekit/helpers'
import { defaultTags } from '../../pages/Photos/filter'
import { PlaceCascader } from '../../pages/Photos/Place'
import { useAsyncFn } from 'react-use'
import { useAllCountries } from '../../hooks/useAllCountries'
import { permissionsStore } from '../../data/permissions'
import { imageService } from '../../api/api-image'

interface Props {
	fixedFolder?: string | undefined
	cdnFolder?: CDNFolders
	onFinishedUpload: (folder: string, responseData: any) => void
	predefinedPlaceId?: string
	predefinedTags?: string[] | undefined
}

export const UploadForm: React.FC<Props> = ({ 
	cdnFolder = CDNFolders.IMAGES, 
	onFinishedUpload, 
	fixedFolder, 
	predefinedPlaceId = undefined,
	predefinedTags = undefined, 
}) => {
  const [form] = Form.useForm()
  const [placeDirectory, setPlaceDirectory] = useState( fixedFolder ? fixedFolder : '')
  const [fileList, setFileList] = useState<UploadFile<any>[]>([])
  const [hasPermissionToCreate] = permissionsStore.getState().hasPermissions(['create:image'])

  const {
    mutate,
    isLoading: isCreateImageLoading,
    error: createImageError,
  } = useMutation(async (formData: FormData) => {
    return imageService.createImage(formData)
  })

  const {
    data: allSlugs,
    isLoading: allSlugsLoading,
    error: allSlugsError,
  } = useQuery(['allSlugs'], async () => {
    const { data } = await placeService.getAllPlaces()
    return data
  })

  const { continentData, isLoading: continentDataLoading, error: continentDataError } = useAllCountries()

  const handleUpload = ({ fileList }: UploadChangeParam<UploadFile<any>>) => {
    if (fileList.length > 1) {
      form.resetFields(['tags'])
    }
    setFileList(fileList)
  }

  const [{ loading }, handleSubmit] = useAsyncFn(
    async (event: any) => {
      event.preventDefault()

	  
      if (fileList?.length) {
        const formData = new FormData()

		let alt:string, country:string, description:string, placeId:string, folder:string, tags:string[];
		if( fixedFolder ) {
        	folder = fixedFolder
			alt = ''
			country = ''
			description = ''
			placeId = predefinedPlaceId ?? ''
		} else {
        	folder = `${cdnFolder}${placeDirectory}`
			const { data: placeDetail } = await placeService.getPlaceDetailByGuid(placeDirectory)
			alt = placeDetail.title
			country = placeDetail?.country
			description =
			placeDetail?.place_type === 'city' ? `${alt}, ${country} - © Travelbay` : `${alt} - © Travelbay`
			placeId = placeDetail?.id?.toString() || ''
		}

		if(predefinedTags) {
			tags = predefinedTags;
		} else {
			tags = form.getFieldValue('tags')?.slice?.(0, 1)
		}

        await Promise.all(
          fileList.map(async (file) => {
            const resizedImage = (await resizeImage(file.originFileObj)) as any
            formData.append('files[]', resizedImage, sanitizeFileOrFolder(file.name))
            formData.append('folder', folder)
            formData.append('placeId', placeId)
            formData.append('tags', tags?.length ? JSON.stringify(tags) : '[]')
            formData.append('alt', form.getFieldValue('alt') || alt)
            formData.append('description', form.getFieldValue('description') || description)
          }),
        )

        mutate(formData, {
          onSuccess: (res) => {
            if (res.data.err) {
              notification.open({
                type: 'error',
                message: 'Something went wrong',
                description: `An error occurred trying to upload photos - ${JSON.stringify(res.data.err)}`,
                placement: 'bottomRight',
              })
              return
            }
            // console.log('Upload complete', JSON.stringify(res))
            notification.open({
              type: 'success',
              message: 'Upload complete',
              description: `${fileList.length} photos uploaded to CDN ${folder}`,
              placement: 'bottomRight',
            })
            setFileList([])
            // setPlaceDirectory('')
            onFinishedUpload(folder, res.data)
            form.resetFields()
          },
          onError: (err) => {
            notification.open({
              type: 'error',
              message: 'Something went wrong',
              description: `An error occurred trying to upload photos - ${JSON.stringify(err)}`,
              placement: 'bottomRight',
            })
          },
        })
      }
    },
    [placeDirectory, fileList, form, mutate],
  )

  return (
    <>
      <Row>
        <div style={{ width: '100%', height: '100%' }}>
          <Upload.Dragger
            beforeUpload={() => false}
            disabled={placeDirectory === ''}
            onChange={handleUpload}
            fileList={fileList}
            multiple
            accept="image/*"
          >
            <div>
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ color: placeDirectory === '' ? '#ccc' : '#40a9ff' }} />
              </p>
              <p className="ant-upload-text">
                {placeDirectory === ''
                  ? 'Select a folder before uploading'
                  : 'Click or drag file to this area to upload'}
              </p>
            </div>
          </Upload.Dragger>
        </div>
      </Row>
      <br />
      <Row>
        <Col span={12}>
          <div style={{ padding: 8 }}>
            <Form form={form} labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
              <Form.Item required label="Folder:" name="folder">
				{ fixedFolder ? (
					<>
						{placeDirectory}

						<Button
						  style={{ marginRight: 0, marginLeft: 16 }}
						  type="primary"
						  htmlType="button"
						  onClick={(e) => hasPermissionToCreate && handleSubmit(e)}
						  loading={loading || isCreateImageLoading}
						  disabled={!hasPermissionToCreate || placeDirectory === '' || fileList.length === 0}
						>
						  Upload
						</Button>
					</>
				) : (
					(allSlugsLoading || continentDataLoading) ? (
					  <Spin />
					) : (
					  <div>
						<PlaceCascader
						  continentData={continentData}
						  allSlugs={allSlugs}
						  guid={placeDirectory}
						  onClear={() => setPlaceDirectory('')}
						  onChange={(val) => setPlaceDirectory((last(val) as string) ?? '') }
						/>
						<Button
						  style={{ marginRight: 0, marginLeft: 16 }}
						  type="primary"
						  htmlType="button"
						  onClick={(e) => hasPermissionToCreate && handleSubmit(e)}
						  loading={loading || isCreateImageLoading}
						  disabled={!hasPermissionToCreate || placeDirectory === '' || fileList.length === 0}
						>
						  Upload
						</Button>
					  </div>
					)
				) }
              </Form.Item>
              <Form.Item label="Tags:" name="tags">
				{ predefinedTags ? (
					<>
						{predefinedTags.join(',')}
					</>
				) : (
					<Select
					disabled={fileList.length > 1}
					mode="multiple"
					allowClear
					placeholder="Select tag(s)"
					options={defaultTags.values}
					/>
				)}
              </Form.Item>
              <Form.Item label="Alt:" name="alt">
                <Input placeholder="Enter image alt text" />
              </Form.Item>
              <Form.Item label="Description:" name="description">
                <Input.TextArea autoSize={true} placeholder="Enter image description" />
              </Form.Item>
            </Form>
          </div>
        </Col>
        <Col span={12}>
          {fileList?.length > 0 && (
            <div style={{ padding: 8 }}>
              <div style={{ marginBottom: 8 }}>Preview:</div>
              <Row gutter={[8, 8]}>
                {fileList.map((file) => (
                  <Col key={file.uid}>
                    <Image
                      style={{ objectFit: 'contain' }}
                      width={100}
                      height={100}
                      src={URL.createObjectURL(file.originFileObj as any)}
                      fallback="data:image/png;base64,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"
                    />
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </Col>
      </Row>
    </>
  )
}
